import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Trophy, Home, Eye, Lock, ArrowRight, Brain, BarChart3, BookOpen, Sparkles, Zap, Award, ChevronDown, ChevronUp, Target, TrendingUp, Share2, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { formatPercentage } from '../../lib/utils';
import { useTranslation } from 'react-i18next';
import { localize } from '../../lib/localize';
import { apiErrorMessage } from '../../lib/apiErrors';
import { shareLink, PUBLIC_URL } from '../../lib/share';
import { BadgeNotification } from '../../components/BadgeNotification';
import { RewardedGate } from '../../components/RewardedGate';
import { GoogleAdBanner } from '../../components/GoogleAds';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { chartTheme, tooltipStyle } from '../../lib/chartTheme';

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${url}`;
};

export function ResultsPage() {
  const { quizIndex } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [result, setResult] = useState(() => {
    const locationResult = location.state?.result;
    if (locationResult) {
      sessionStorage.setItem(`quiz_result_${quizIndex}`, JSON.stringify(locationResult));
      return locationResult;
    }
    const stored = sessionStorage.getItem(`quiz_result_${quizIndex}`);
    return stored ? JSON.parse(stored) : null;
  });
  
  const [showAnswers, setShowAnswers] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);
  const [newBadges, setNewBadges] = useState([]);
  const [showAdGate, setShowAdGate] = useState(false);
  const [locking, setLocking] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [loadingExplanation, setLoadingExplanation] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  // Hooks must run before any early return below (React rules of hooks)
  const [reportTarget, setReportTarget] = useState(null);
  const [reported, setReported] = useState({});
  
  useEffect(() => {
    const fetchResult = async () => {
      if (!result && quizIndex) {
        setLoadingResult(true);
        try {
          const res = await userAPI.getQuizResult(quizIndex);
          if (res.data) {
            setResult(res.data);
            sessionStorage.setItem(`quiz_result_${quizIndex}`, JSON.stringify(res.data));
          }
        } catch (error) {
          console.error('Failed to fetch result:', error);
        } finally {
          setLoadingResult(false);
        }
      }
    };
    fetchResult();
  }, [result, quizIndex]);

  // Fetch analysis data
  useEffect(() => {
    const fetchAnalysis = async () => {
      if (result && quizIndex) {
        setLoadingAnalysis(true);
        try {
          const res = await userAPI.getAssessmentAnalysis(quizIndex);
          setAnalysis(res.data);
        } catch (error) {
          console.error('Failed to fetch analysis:', error);
        } finally {
          setLoadingAnalysis(false);
        }
      }
    };
    fetchAnalysis();
  }, [result, quizIndex, i18n.language]);
  
  useEffect(() => {
    const badges = result?.badges_earned || result?.new_badges || [];
    if (badges.length > 0) {
      setNewBadges(badges);
      badges.forEach((badge, idx) => {
        setTimeout(() => {
          toast.success(t('badge.earnedToast', { name: localize(badge.name, i18n.language) || badge.badge_id }), { duration: 5000 });
        }, idx * 1000);
      });
    }
  }, [result]);

  const isPerfect = result?.score?.correct_count === result?.score?.total;
  const isQuizLocked = result?.is_locked || result?.locked;

  const handleViewAnswers = async () => {
    if (showAnswers) {
      setShowAnswers(false);
      return;
    }
    if (!isQuizLocked) {
      setShowAdGate(true);
      return;
    }
    await loadAnswers();
  };
  
  const handleAdComplete = async () => {
    setShowAdGate(false);
    setLocking(true);
    try {
      await userAPI.lockQuiz(quizIndex);
      setResult(prev => {
        const updated = { ...prev, is_locked: true, locked: true };
        sessionStorage.setItem(`quiz_result_${quizIndex}`, JSON.stringify(updated));
        return updated;
      });
      await loadAnswers();
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'results.lockFailed'));
      setLocking(false);
    }
  };
  
  const loadAnswers = async () => {
    setLoadingAnswers(true);
    try {
      const baseLang = i18n?.language?.split('-')[0]?.split('@')[0] || 'en';
      const lang = ['en', 'sk'].includes(baseLang) ? baseLang : 'en';
      const res = await userAPI.getAnswers(quizIndex, lang);
      setQuestions(res.data.questions);
      setShowAnswers(true);
      setActiveTab('review');
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'results.answersFailed'));
    } finally {
      setLoadingAnswers(false);
      setLocking(false);
    }
  };

  const handleAiExplain = async (question) => {
    const qId = question.question_id || question._id;
    if (explanations[qId] || loadingExplanation[qId]) return;
    
    setLoadingExplanation(prev => ({ ...prev, [qId]: true }));
    try {
      const res = await userAPI.getAiExplanation({
        question_text: question.question_text || question.text,
        user_answer: question.user_answer || '',
        correct_answer: question.correct_answer || '',
        topic: question.topic || 'General Knowledge',
        language: String(i18n.language || 'en').slice(0, 2)
      });
      setExplanations(prev => ({ ...prev, [qId]: res.data.explanation }));
    } catch (error) {
      setExplanations(prev => ({ ...prev, [qId]: t('results.explainFailed') }));
    } finally {
      setLoadingExplanation(prev => ({ ...prev, [qId]: false }));
    }
  };

  if (!result) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400 mb-4">{t('results.noResults')}</p>
        <Button onClick={() => navigate('/home')} className="bg-teal-500 text-slate-950 hover:bg-teal-400">
          <Home className="w-4 h-4 mr-2" />
          {t('results.goHome')}
        </Button>
      </div>
    );
  }
  
  if (showAdGate) {
    return (
      <RewardedGate
        onComplete={handleAdComplete}
        onCancel={() => setShowAdGate(false)}
        title={t('results.reviewAnswers', 'Review Answers')}
        description={t('results.watchToUnlock', 'Watch to unlock answer review')}
      />
    );
  }


  const handleReport = async (questionId, reason) => {
    try {
      await userAPI.reportQuestion(questionId, { reason });
      setReported(prev => ({ ...prev, [questionId]: true }));
      setReportTarget(null);
      toast.success(t('report.thanks', 'Thanks! Our team will review this question.'));
    } catch (e) {
      toast.error(apiErrorMessage(e, t, 'errors.generic'));
    }
  };

  const handleShare = async () => {
    const sc = result.score || {};
    const text = t('share.resultText', {
      correct: sc.correct_count, total: sc.total, pct: formatPercentage(sc.percentage),
      defaultValue: `I scored ${sc.correct_count}/${sc.total} (${formatPercentage(sc.percentage)}%) on today's SocraQuest assessment! Can you beat me? 🧠`,
    });
    const res = await shareLink({ title: 'SocraQuest', text, url: PUBLIC_URL });
    if (res === 'copied') toast.success(t('share.copied', 'Copied to clipboard!'));
  };

  const score = result.score || {};
  const xpInfo = result.xp || {};
  const topicBreakdown = analysis?.topic_breakdown || [];
  const incorrectQuestions = analysis?.incorrect_questions || [];

  // Difficulty analysis: prefer real question-level metadata (difficulty_breakdown
  // from the backend); fall back to inference from topic accuracy when questions
  // carry no difficulty field.
  const realDifficulty = analysis?.difficulty_breakdown || [];
  const difficultyData = realDifficulty.length > 0 ? (() => {
    const colorMap = { easy: chartTheme.seafoam, medium: chartTheme.amber, hard: chartTheme.rose };
    const labelMap = { easy: t('results.easy'), medium: t('results.medium'), hard: t('results.hard') };
    return realDifficulty.map(d => ({
      name: `${labelMap[d.difficulty] || d.difficulty} (${d.accuracy}%)`,
      value: d.total,
      color: colorMap[d.difficulty] || chartTheme.amber
    }));
  })() : topicBreakdown.length > 0 ? (() => {
    const hard = topicBreakdown.filter(t => t.accuracy < 40).length;
    const medium = topicBreakdown.filter(t => t.accuracy >= 40 && t.accuracy < 75).length;
    const easy = topicBreakdown.filter(t => t.accuracy >= 75).length;
    return [
      { name: t('results.challenging'), value: hard, color: chartTheme.rose },
      { name: t('results.moderate'), value: medium, color: chartTheme.amber },
      { name: t('results.mastered'), value: easy, color: chartTheme.seafoam }
    ].filter(d => d.value > 0);
  })() : [];

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-8 px-4 sm:px-6" data-testid="results-page">
      {/* Badge Notifications */}
      {newBadges.map((badge, idx) => (
        <BadgeNotification key={badge.id} badge={badge} onClose={() => setNewBadges(prev => prev.filter((_, i) => i !== idx))} />
      ))}

      {/* Score Hero Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Card className={`rounded-2xl p-6 sm:p-8 border ${
          isPerfect
            ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30'
            : 'bg-slate-900/60 border-slate-800'
        }`} data-testid="score-card">
          {isPerfect && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-5xl mb-3 text-center">🎉</motion.div>}
          
          <h1 className="text-2xl sm:text-3xl font-semibold text-white text-center font-['Space_Grotesk'] tracking-tight" data-testid="result-title">
            {isPerfect ? t('results.perfectScore', 'Perfect Score!') : t('results.assessmentComplete', 'Assessment Complete')}
          </h1>
          
          {/* Score Metrics */}
          <div className="flex items-center justify-center gap-6 sm:gap-10 my-6">
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold font-['Azeret_Mono'] text-teal-300" data-testid="score-fraction">
                {score.correct_count}/{score.total}
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{t('results.correct', 'Correct')}</p>
            </div>
            <div className="w-px h-14 bg-slate-700/50" />
            <div className="text-center">
              <p className={`text-3xl sm:text-4xl font-bold font-['Azeret_Mono'] ${
                score.percentage >= 80 ? 'text-emerald-400' : score.percentage >= 50 ? 'text-amber-400' : 'text-rose-400'
              }`} data-testid="score-percentage">
                {formatPercentage(score.percentage)}%
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{t('kpi.accuracy', 'Accuracy')}</p>
            </div>
            {result.rank && (
              <>
                <div className="w-px h-14 bg-slate-700/50" />
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold font-['Azeret_Mono'] text-amber-400">#{result.rank}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{t('results.rank', 'Rank')}</p>
                </div>
              </>
            )}
          </div>

          {/* XP Earned Banner */}
          {xpInfo.xp_earned > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4"
              data-testid="xp-earned-banner"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                <span className="text-amber-200 font-semibold font-['Azeret_Mono']">+{xpInfo.xp_earned} XP</span>
              </div>
              <div className="w-px h-6 bg-amber-500/20" />
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300/80 text-sm">{t('dashboard.level', 'Level')} {xpInfo.level} {xpInfo.level_title}</span>
              </div>
              {xpInfo.leveled_up && (
                <Badge className="bg-amber-500 text-slate-950 text-xs">{t('results.levelUp', 'Level Up!')}</Badge>
              )}
            </motion.div>
          )}

          {/* Locked Badge */}
          {isQuizLocked && (
            <div className="flex justify-center mb-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800/60 rounded-full text-slate-400 text-xs">
                <Lock className="w-3.5 h-3.5" /> {t('results.assessmentLocked', 'Assessment Locked')}
              </div>
            </div>
          )}

          {/* Badges Earned */}
          {newBadges.length > 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/20 rounded-xl p-4 mb-4">
              <h3 className="text-amber-400 font-semibold text-sm mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4" /> {t('results.badgesEarned', 'Badges Earned')}
              </h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {newBadges.map((badge, idx) => (
                  <motion.div key={badge.badge_id || idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.15 }} className="flex flex-col items-center bg-slate-800/50 rounded-lg p-2 min-w-[80px]">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl mb-1"
                      style={{ backgroundColor: `${badge.color || '#f59e0b'}20` }}>
                      {badge.icon === 'Trophy' ? '🏆' : badge.icon === 'Star' ? '⭐' : badge.icon === 'Flame' ? '🔥' :
                       badge.icon === 'Zap' ? '⚡' : badge.icon === 'Crown' ? '👑' : '🏅'}
                    </div>
                    <span className="text-white font-medium text-xs text-center">{localize(badge.name, i18n.language) || badge.badge_id}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2.5 justify-center mt-4">
            <Button onClick={handleViewAnswers} disabled={loadingAnswers || locking}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl"
              data-testid="view-answers-btn">
              {loadingAnswers || locking ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showAnswers ? t('results.hideAnswers', 'Hide Answers') : t('results.reviewAnswers', 'Review Answers')}
            </Button>
            <Button onClick={() => navigate('/rankings')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl"
              data-testid="view-rankings-btn">
              <Trophy className="w-4 h-4 mr-2" /> {t('nav.rankings', 'Leaderboard')}
            </Button>
            <Button onClick={handleShare} variant="outline"
              className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white font-semibold rounded-xl"
              data-testid="share-result-btn">
              <Share2 className="w-4 h-4 mr-2" /> {t('share.button', 'Share')}
            </Button>
            <Button onClick={() => navigate('/home')}
              className="bg-teal-500 text-slate-950 hover:bg-teal-400 font-semibold rounded-xl"
              data-testid="next-quiz-btn">
              {t('results.nextAssessment', 'Next Assessment')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Assessment Report Tabs */}
      {(showAnswers || analysis) && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-900/60 border border-slate-800 rounded-xl p-1 w-full grid grid-cols-3">
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-300 rounded-lg text-xs sm:text-sm"
                data-testid="tab-overview">
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> {t('results.overview', 'Overview')}
              </TabsTrigger>
              <TabsTrigger value="topics" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-300 rounded-lg text-xs sm:text-sm"
                data-testid="tab-topics">
                <Target className="w-3.5 h-3.5 mr-1.5" /> {t('results.topics')}
              </TabsTrigger>
              <TabsTrigger value="review" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-300 rounded-lg text-xs sm:text-sm"
                data-testid="tab-review">
                <Brain className="w-3.5 h-3.5 mr-1.5" /> {t('results.aiReview', 'AI Review')}
              </TabsTrigger>
            </TabsList>

            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              {loadingAnalysis ? (
                <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-6 flex justify-center">
                  <LoadingSpinner className="w-6 h-6" />
                </Card>
              ) : analysis ? (
                <>
                  {/* Topic Performance Chart */}
                  {topicBreakdown.length > 0 && (
                    <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="topic-breakdown-chart">
                      <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-teal-400" /> {t('results.topicPerformance')}
                      </h3>
                      <div className="h-[220px] w-full">
                        <ResponsiveContainer>
                          <BarChart data={topicBreakdown.map(t => ({
                            name: t.topic.length > 15 ? t.topic.slice(0, 15) + '...' : t.topic,
                            fullName: t.topic,
                            accuracy: t.accuracy,
                            correct: t.correct,
                            total: t.total
                          }))} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} horizontal={false} />
                            <XAxis type="number" domain={[0, 100]} tick={{ fill: chartTheme.text, fontSize: 11 }} tickFormatter={v => `${v}%`} />
                            <YAxis dataKey="name" type="category" width={110} tick={{ fill: chartTheme.text, fontSize: 11 }} />
                            <Tooltip {...tooltipStyle} formatter={(v, name, props) => [`${v}% (${props.payload.correct}/${props.payload.total})`, props.payload.fullName]} />
                            <Bar dataKey="accuracy" radius={[0, 6, 6, 0]} maxBarSize={24}>
                              {topicBreakdown.map((entry, i) => (
                                <Cell key={i} fill={entry.accuracy >= 80 ? chartTheme.seafoam : entry.accuracy >= 50 ? chartTheme.amber : chartTheme.rose} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  )}

                  {/* Difficulty Distribution + Summary Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {difficultyData.length > 0 && (
                      <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="difficulty-analysis">
                        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-amber-400" /> {t('results.difficultyAnalysis', 'Difficulty Analysis')}
                        </h3>
                        <div className="h-[160px] w-full">
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie data={difficultyData} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                                paddingAngle={4} dataKey="value" nameKey="name" stroke="none">
                                {difficultyData.map((entry, i) => (
                                  <Cell key={i} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip {...tooltipStyle} formatter={(v, name) => [`${v} topic(s)`, name]} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap gap-3 justify-center mt-2">
                          {difficultyData.map((d, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                              <span className="text-xs text-slate-400">{d.name} ({d.value})</span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Summary Stats */}
                    <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="assessment-summary">
                      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-teal-400" /> {t('results.assessmentSummary', 'Assessment Summary')}
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-400">{t('results.topicsCovered', 'Topics Covered')}</span>
                          <span className="text-sm font-['Azeret_Mono'] text-white">{topicBreakdown.length}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-400">{t('results.questionsAnswered', 'Questions Answered')}</span>
                          <span className="text-sm font-['Azeret_Mono'] text-white">{score.total || 30}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-400">{t('results.incorrectAnswers', 'Incorrect Answers')}</span>
                          <span className="text-sm font-['Azeret_Mono'] text-rose-400">{incorrectQuestions.length}</span>
                        </div>
                        {analysis.time_ms > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-400">{t('results.timeSpent', 'Time Spent')}</span>
                            <span className="text-sm font-['Azeret_Mono'] text-white">
                              {Math.floor(analysis.time_ms / 60000)}m {Math.floor((analysis.time_ms % 60000) / 1000)}s
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-400">{t('results.attempt', 'Attempt')}</span>
                          <span className="text-sm font-['Azeret_Mono'] text-white">#{analysis.attempt_number || result.attempt_number || 1}</span>
                        </div>
                      </div>
                      <Button onClick={() => navigate('/learning')} variant="ghost" size="sm"
                        className="w-full mt-4 text-teal-400 hover:text-teal-300 border border-slate-700 hover:border-teal-500/30">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t('results.viewLearningPath', 'View Learning Path')}
                      </Button>
                    </Card>
                  </div>
                </>
              ) : (
                <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 text-center">
                  <BarChart3 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">{t('results.analysisPending', 'Analysis data will appear after your assessment is processed')}</p>
                </Card>
              )}
            </TabsContent>

            {/* TOPICS TAB */}
            <TabsContent value="topics" className="mt-4 space-y-3">
              {topicBreakdown.length > 0 ? (
                topicBreakdown.map((topic, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card className="rounded-xl bg-slate-900/60 border border-slate-800 p-4" data-testid={`topic-card-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            topic.accuracy >= 80 ? 'bg-emerald-500/15 text-emerald-400' :
                            topic.accuracy >= 50 ? 'bg-amber-500/15 text-amber-400' :
                            'bg-rose-500/15 text-rose-400'
                          }`}>
                            <Target className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-medium text-white truncate">{topic.topic}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-slate-500 font-['Azeret_Mono']">{topic.correct}/{topic.total}</span>
                          <span className={`text-sm font-semibold font-['Azeret_Mono'] ${
                            topic.accuracy >= 80 ? 'text-emerald-400' : topic.accuracy >= 50 ? 'text-amber-400' : 'text-rose-400'
                          }`}>{topic.accuracy}%</span>
                        </div>
                      </div>
                      <Progress value={topic.accuracy} className="h-1.5 bg-slate-800" />
                      <div className="mt-2">
                        <Badge variant="outline" className={`text-xs ${
                          topic.accuracy >= 80 ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10' :
                          topic.accuracy >= 50 ? 'text-amber-300 border-amber-500/30 bg-amber-500/10' :
                          'text-rose-300 border-rose-500/30 bg-rose-500/10'
                        }`}>
                          {topic.accuracy >= 80 ? t('results.mastered') : topic.accuracy >= 50 ? t('results.developing') : t('results.needsFocus')}
                        </Badge>
                      </div>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 text-center">
                  <Target className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">{t('results.topicAnalysisUnavailable', 'Topic analysis not available')}</p>
                </Card>
              )}
            </TabsContent>

            {/* AI REVIEW TAB */}
            <TabsContent value="review" className="mt-4 space-y-4">
              {showAnswers && questions.length > 0 ? (
                <div className="space-y-4">
                  {questions.map((q, idx) => {
                    const qId = q._id || idx;
                    const isIncorrect = !q.is_correct;
                    return (
                      <Card key={qId} className="rounded-xl bg-slate-900/60 border border-slate-800 p-4 sm:p-5" data-testid={`answer-card-${idx}`}>
                        {/* Question Image */}
                        {q.image_url && (
                          <div className="mb-3 rounded-xl overflow-hidden border border-slate-700 bg-black">
                            <img src={resolveImageUrl(q.image_url)} alt={t('quiz.imageAlt')}
                              className="w-full max-h-44 object-contain mx-auto"
                              onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        
                        <div className="flex items-start gap-3 mb-3">
                          <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                            q.is_correct ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <h3 className="font-medium text-sm text-white flex-1 leading-relaxed">{q.text}</h3>
                          {q._id && (
                            reported[q._id] ? (
                              <span className="text-[10px] text-slate-500 flex items-center gap-1 shrink-0"><Flag className="w-3 h-3" /> {t('report.sent', 'Reported')}</span>
                            ) : (
                              <button onClick={() => setReportTarget(reportTarget === q._id ? null : q._id)}
                                title={t('report.button', 'Report this question')}
                                className="text-slate-600 hover:text-rose-400 transition-colors shrink-0 p-1"
                                data-testid={`report-btn-${idx}`}>
                                <Flag className="w-3.5 h-3.5" />
                              </button>
                            )
                          )}
                        </div>
                        {reportTarget === q._id && !reported[q._id] && (
                          <div className="ml-10 mb-3 p-3 rounded-xl bg-slate-800/60 border border-slate-700">
                            <p className="text-xs text-slate-400 mb-2">{t('report.question', 'What is wrong with this question?')}</p>
                            <div className="flex flex-wrap gap-2">
                              {[
                                ['wrong_answer', t('report.wrongAnswer', 'Wrong answer')],
                                ['typo', t('report.typo', 'Typo / wording')],
                                ['unclear', t('report.unclear', 'Unclear')],
                                ['other', t('report.other', 'Other')],
                              ].map(([key, label]) => (
                                <button key={key} onClick={() => handleReport(q._id, key)}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-700/70 text-slate-200 hover:bg-rose-500/20 hover:text-rose-300 transition-colors">
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="ml-10 space-y-1.5">
                          {q.options?.map((opt, optIdx) => {
                            const optKey = String.fromCharCode(65 + optIdx);
                            const isCorrect = optKey === q.correct_key;
                            const isUserAnswer = optKey === q.user_answer;
                            const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.text || '');
                            
                            return (
                              <div key={optIdx} className={`px-3 py-2 rounded-lg text-xs ${
                                isCorrect ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-300' :
                                isUserAnswer && !isCorrect ? 'bg-rose-500/15 border border-rose-500/25 text-rose-300' :
                                'bg-slate-800/40 text-slate-400'
                              }`}>
                                <span className="font-bold mr-1.5">{optKey}.</span>
                                {optLabel}
                                {isCorrect && <span className="ml-1.5 text-emerald-400">✓</span>}
                                {isUserAnswer && !isCorrect && <span className="ml-1.5 text-rose-400">✗</span>}
                              </div>
                            );
                          })}
                        </div>

                        {/* AI Explanation for incorrect */}
                        {isIncorrect && (
                          <div className="ml-10 mt-3">
                            {explanations[qId] ? (
                              <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                                className="p-3 rounded-xl bg-teal-500/8 border border-teal-500/20" data-testid={`ai-explanation-${idx}`}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Brain className="w-3.5 h-3.5 text-teal-400" />
                                  <span className="text-xs font-semibold text-teal-300">{t('results.aiExplanation', 'AI Explanation')}</span>
                                </div>
                                <p className="text-xs text-slate-300 leading-relaxed">{explanations[qId]}</p>
                              </motion.div>
                            ) : (
                              <Button onClick={() => handleAiExplain({
                                question_id: qId,
                                question_text: q.text,
                                user_answer: q.options?.find((o, oi) => String.fromCharCode(65 + oi) === q.user_answer)?.label || q.user_answer || '',
                                correct_answer: q.options?.find((o, oi) => String.fromCharCode(65 + oi) === q.correct_key)?.label || q.correct_key || '',
                                topic: q.topic || 'General'
                              })}
                                disabled={loadingExplanation[qId]}
                                variant="ghost" size="sm"
                                className="text-teal-400 hover:text-teal-300 border border-teal-500/20 hover:border-teal-500/40 rounded-lg h-8 text-xs"
                                data-testid={`explain-btn-${idx}`}>
                                {loadingExplanation[qId] ? (
                                  <><LoadingSpinner className="w-3 h-3 mr-1.5" /> {t('results.analyzing', 'Analyzing...')}</>
                                ) : (
                                  <><Brain className="w-3 h-3 mr-1.5" /> {t('results.explainWithAI', 'Explain with AI')}</>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-8 text-center">
                  <Eye className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-3">{t('results.reviewHint', 'Tap "Review Answers" above to see detailed answers with AI explanations')}</p>
                  <Button onClick={handleViewAnswers} disabled={loadingAnswers || locking}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-xl text-sm">
                    {loadingAnswers || locking ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {t('results.reviewAnswers', 'Review Answers')}
                  </Button>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      )}

      {/* Go to Dashboard CTA */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <Card className="rounded-2xl bg-slate-900/40 border border-slate-800/50 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-white">{t('results.trackProgress', 'Track your learning progress')}</p>
            <p className="text-xs text-slate-500">{t('results.viewAnalytics', 'View detailed analytics and recommendations')}</p>
          </div>
          <Button onClick={() => navigate('/dashboard')} size="sm"
            className="bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 border border-teal-500/20 rounded-xl">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> {t('nav.insights', 'Insights')}
          </Button>
        </Card>
      </motion.div>
      <GoogleAdBanner />
    </div>
  );
}
